import type { Assessment } from "../assessment-types";

export const NUMBNESS_ASSESSMENT: Assessment = {
  slug: "numbness",
  emoji: "🕯️",
  title: "Emotional Numbness & Shutdown",
  tagline: "When feeling less is how you cope",
  intro:
    "This reflects on times when feeling less has become a way of coping — flatness, distance, or a sense of going through the motions. This is often the body and mind's way of protecting you from being overwhelmed.",
  questions: [
    { id: "q1", text: "Do you feel emotionally flat, even in situations that would usually bring up feeling?" },
    { id: "q2", text: "Do you find it hard to feel joy or excitement, even about things you used to enjoy?" },
    { id: "q3", text: "Do you go through your day feeling like you're just going through the motions?" },
    { id: "q4", text: "Do you struggle to cry or feel sadness, even when something sad happens?" },
    { id: "q5", text: "Do you feel numb rather than upset after something stressful or hurtful happens?" },
    { id: "q6", text: "Do you find yourself avoiding closeness because feeling connected takes too much energy?" },
    { id: "q7", text: "Do you notice yourself withdrawing or going quiet when things get emotionally intense?" },
    { id: "q8", text: "Do you feel disconnected from your own wants and needs?" },
    { id: "q9", text: "Do you find it hard to feel motivated, even about things that matter to you?" },
    { id: "q10", text: "Do people tell you that you seem distant or hard to read?" },
    { id: "q11", text: "Do you feel like you're watching your life happen rather than living it?" },
    { id: "q12", text: "Do you use busyness, screens or habits to avoid feeling much at all?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "You seem to stay in touch with your feelings most of the time.",
      body: "This suggests a fairly open relationship with your emotional world. It's still worth noticing the moments when things feel flatter than usual.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns of feeling flat or going through the motions at times.",
      body: "This is a common response to stress or tiredness, and it can come and go. Small check-ins with yourself through the day can help you notice when numbness starts to creep in.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a pattern of feeling emotionally flat or shut down fairly often.",
      body: "This kind of numbing often develops as protection, when feeling fully felt unsafe or exhausting at some point. It made sense as a way of getting through. Gently reconnecting with small sensations and feelings, at your own pace, can help soften it over time.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong pattern of emotional numbness or shutdown.",
      body: "Feeling this disconnected can be lonely, even if it once felt protective. It often reflects a history where emotions were overwhelming or unsafe to fully feel. This is an adaptation your system built for good reason, and reconnection is possible gradually and gently.",
    },
  ],
  next: [
    { label: "Understanding trauma responses", to: "/learn/trauma-responses" },
    { label: "The window of tolerance", to: "/learn/window-of-tolerance" },
    { label: "Try a somatic practice", to: "/somatic" },
    { label: "Reflect in a guided journal", to: "/guided-journal" },
  ],
};
